import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { useFormatting } from "@msys/ui";
import { Link, Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { isNil } from "lodash-es";
import React, { Dispatch, SetStateAction } from "react";
import {
  OrganisationQuoteTemplatesFilters,
  ProjectType,
  TypeOfTemplate,
} from "../../../../clients/graphqlTypes.js";
import {
  FilterChip,
  FilterChipGroup,
} from "../../../commons/filters/FilterChip.js";
import { useCategories } from "../../categories/useCategories.js";
import { useProjectTypes } from "../../projects/useProjectTypes.js";
import { PropertiesFilterChips } from "../../properties/PropertiesFilterChips.js";
import { TemplateFiltersFormValues } from "../filters/TemplateFilterFields.js";
import { useTemplatesQuoteFilterChips_TemplateTypesQuery } from "./TemplatesQuoteFilterChips.generated.js";
import { useTypeOfTemplates } from "./useTypeOfTemplates.js";

type Filters = Pick<
  OrganisationQuoteTemplatesFilters,
  | "createdAfter"
  | "createdBefore"
  | "priceMin"
  | "priceMax"
  | "tagsAny"
  | "tagsAll"
>;

interface Props<F extends Filters> {
  filters: F;
  setFilters: Dispatch<SetStateAction<F>>;
  templateFilters?: TemplateFiltersFormValues;
  setTemplateFilters?: Dispatch<SetStateAction<TemplateFiltersFormValues>>;
  onReset?(): void | Promise<void>;
  fixedFilters?: Partial<F> & Partial<TemplateFiltersFormValues>;
}

export const TemplatesQuoteFilterChips = <F extends Filters>({
  filters,
  setFilters,
  templateFilters,
  setTemplateFilters,
  fixedFilters,
  onReset,
}: Props<F>) => {
  const { t } = useTranslate(["Templates", "Global"]);
  const { getFormattedDate, getFormattedPrice } = useFormatting();

  const { projectTypeLabels } = useProjectTypes();
  const { typeOfTemplatesLabels } = useTypeOfTemplates();
  const { resolveCategories } = useCategories();

  const client = useApolloClient();
  const templateTypesQuery = useTemplatesQuoteFilterChips_TemplateTypesQuery({
    client,
    variables: {
      ids: templateFilters?.templateTypeIds ?? [],
    },
    fetchPolicy: "cache-and-network",
    skip:
      !templateFilters?.templateTypeIds ||
      templateFilters.templateTypeIds.length === 0,
  });

  const templateTypes = React.useMemo(
    () =>
      getDataOrNull(
        (templateTypesQuery.data ?? templateTypesQuery.previousData)
          ?.templateTypes
      )?.templateTypes ?? [],
    [templateTypesQuery.data, templateTypesQuery.previousData]
  );

  const categories = React.useMemo(
    () =>
      !templateFilters?.categories || templateFilters.categories.length === 0
        ? []
        : resolveCategories(templateFilters.categories, {
            addParentsWhenNotIncluded: false,
            removeChildrenIfIncluded: false,
            excludeDeletedCategories: true,
          }),
    [resolveCategories, templateFilters?.categories]
  );

  const getTemplateTypeTitle = (id: string) =>
    templateTypes.find(templateType => templateType.id === id)?.title;

  const getCategoryTitle = (id: string) =>
    categories.find(c => c.id === id)?.name;

  const getTypeOfProjectTitle = (value: ProjectType) =>
    projectTypeLabels[value];

  const getTypeOfTemplateTitle = (value: TypeOfTemplate) =>
    typeOfTemplatesLabels[value];

  const getTemplateTypePropertyTitle = (key: string) => {
    for (const templateType of templateTypes) {
      const prop = templateType.props2.find(p => p.key === key);
      if (prop) return prop.label;
    }
  };

  return canShowChips(filters, templateFilters) ? (
    <Stack
      direction={"row"}
      spacing={1}
      flexWrap="wrap"
      alignItems="center"
      useFlexGap
    >
      {templateFilters?.templateTypeIds &&
        templateFilters.templateTypeIds.length > 0 && (
          <FilterChipGroup
            values={templateFilters.templateTypeIds}
            setValues={
              isNil(fixedFilters?.templateTypeIds)
                ? newValues => {
                    setTemplateFilters?.(filters => ({
                      ...filters,
                      templateTypeIds:
                        newValues && newValues.length > 0 ? newValues : null,
                    }));
                  }
                : undefined
            }
            getValueLabel={getTemplateTypeTitle}
          />
        )}
      {templateFilters?.categories && templateFilters.categories.length > 0 && (
        <FilterChipGroup
          values={templateFilters.categories}
          setValues={
            isNil(fixedFilters?.categories)
              ? newValues => {
                  setTemplateFilters?.(filters => ({
                    ...filters,
                    categories:
                      newValues && newValues.length > 0 ? newValues : null,
                  }));
                }
              : undefined
          }
          getValueLabel={getCategoryTitle}
        />
      )}
      {templateFilters?.typeOfProjects &&
        templateFilters.typeOfProjects.length > 0 && (
          <FilterChipGroup<ProjectType>
            values={templateFilters.typeOfProjects}
            setValues={
              isNil(fixedFilters?.typeOfProjects)
                ? newValues => {
                    setTemplateFilters?.(filters => ({
                      ...filters,
                      typeOfProjects:
                        newValues && newValues.length > 0 ? newValues : null,
                    }));
                  }
                : undefined
            }
            getValueLabel={getTypeOfProjectTitle}
          />
        )}
      {templateFilters?.typeOfTemplates &&
        templateFilters.typeOfTemplates.length > 0 && (
          <FilterChipGroup<TypeOfTemplate>
            values={templateFilters.typeOfTemplates}
            setValues={
              isNil(fixedFilters?.typeOfTemplates)
                ? newValues => {
                    setTemplateFilters?.(filters => ({
                      ...filters,
                      typeOfTemplates:
                        newValues && newValues.length > 0 ? newValues : null,
                    }));
                  }
                : undefined
            }
            getValueLabel={getTypeOfTemplateTitle}
          />
        )}
      <FilterChip
        label={t("Created after")}
        onDelete={
          isNil(fixedFilters?.createdAfter)
            ? () =>
                setFilters(filters => ({
                  ...filters,
                  createdAfter: null, // FIXME: remove instead
                }))
            : undefined
        }
        value={filters.createdAfter && getFormattedDate(filters.createdAfter)}
      />
      <FilterChip
        label={t("Created before")}
        onDelete={
          isNil(fixedFilters?.createdBefore)
            ? () =>
                setFilters(filters => ({
                  ...filters,
                  createdBefore: null, // FIXME: remove instead
                }))
            : undefined
        }
        value={filters.createdBefore && getFormattedDate(filters.createdBefore)}
      />
      <FilterChip
        label={t("Min price")}
        onDelete={
          isNil(fixedFilters?.priceMin)
            ? () =>
                setFilters(filters => ({
                  ...filters,
                  priceMin: null, // FIXME: remove instead
                }))
            : undefined
        }
        value={filters.priceMin}
        getValueLabel={value => getFormattedPrice(value) ?? ""}
      />
      <FilterChip
        label={t("Max price")}
        onDelete={
          isNil(fixedFilters?.priceMax)
            ? () =>
                setFilters(filters => ({
                  ...filters,
                  priceMax: null, // FIXME: remove instead
                }))
            : undefined
        }
        value={filters.priceMax}
        getValueLabel={value => getFormattedPrice(value) ?? ""}
      />
      <FilterChipGroup
        label={t("Tag", { ns: "Templates" })}
        values={filters.tagsAny}
        getValueLabel={value => value}
        setValues={
          isNil(fixedFilters?.tagsAny)
            ? value =>
                setFilters(state => ({
                  ...state,
                  tagsAny: value,
                }))
            : undefined
        }
      />
      <FilterChip
        label={t("Tags (all)", { ns: "Templates" })}
        onDelete={
          isNil(fixedFilters?.tagsAll)
            ? () =>
                setFilters(filters => ({
                  ...filters,
                  tagsAll: null, // FIXME: remove instead
                }))
            : undefined
        }
        value={filters.tagsAll?.join?.(",")}
        getValueLabel={value => value}
      />
      {templateFilters?.properties && (
        <PropertiesFilterChips
          values={templateFilters?.properties}
          setValues={
            isNil(fixedFilters?.properties)
              ? newValue =>
                  setTemplateFilters?.(filters => ({
                    ...filters,
                    properties: newValue ?? null,
                  }))
              : undefined
          }
          getKeyLabel={getTemplateTypePropertyTitle}
        />
      )}
      {onReset && canResetFilters(filters, templateFilters, fixedFilters) && (
        <Typography variant="body2">
          <Link onClick={onReset} color="secondary" sx={{ cursor: "pointer" }}>
            {t("Clear all", { ns: "Global" })}
          </Link>
        </Typography>
      )}
    </Stack>
  ) : null;
};

export const canShowChips = (
  filters: Filters,
  templateFilters?: TemplateFiltersFormValues
): boolean =>
  Boolean(
    !isNil(filters.priceMin) ||
      !isNil(filters.priceMax) ||
      !isNil(filters.createdAfter) ||
      !isNil(filters.createdBefore) ||
      filters.tagsAny?.length ||
      filters.tagsAll?.length ||
      templateFilters?.templateTypeIds?.length ||
      templateFilters?.categories?.length ||
      templateFilters?.typeOfProjects?.length ||
      templateFilters?.typeOfTemplates?.length ||
      templateFilters?.properties?.length
  );

export const canResetFilters = (
  filters: Filters,
  templateFilters?: TemplateFiltersFormValues,
  fixedFilters?: Partial<Filters> & Partial<TemplateFiltersFormValues>
): boolean =>
  Boolean(
    (!isNil(filters.priceMin) && isNil(fixedFilters?.priceMin)) ||
      (!isNil(filters.priceMax) && isNil(fixedFilters?.priceMax)) ||
      (!isNil(filters.createdAfter) && isNil(fixedFilters?.createdAfter)) ||
      (!isNil(filters.createdBefore) && isNil(fixedFilters?.createdBefore)) ||
      (filters.tagsAny?.length && isNil(fixedFilters?.tagsAny)) ||
      (filters.tagsAll?.length && isNil(fixedFilters?.tagsAll)) ||
      (templateFilters?.templateTypeIds?.length &&
        isNil(fixedFilters?.templateTypeIds)) ||
      (templateFilters?.categories?.length &&
        isNil(fixedFilters?.categories)) ||
      (templateFilters?.typeOfProjects?.length &&
        isNil(fixedFilters?.typeOfProjects)) ||
      (templateFilters?.typeOfTemplates?.length &&
        isNil(fixedFilters?.typeOfTemplates)) ||
      (templateFilters?.properties?.length && isNil(fixedFilters?.properties))
  );
